<template>
  <div
    ref="partialDiv"
    :class="{
      'uss-plan-selection mt-6': true,
    }"
  >
    <div
      :class="{
        'container mx-auto': true,
      }"
    >
      <div
        :class="{
          'flex flex-col justify-center': true,
        }"
      >
        <h2
          :class="{
            'mb-0': true,
          }"
        >
          {{ title }}
        </h2>
        <component
          :is="getComponent(subtitle?._type)"
          v-if="subtitle?._type"
          :value="subtitle"
          :class="{
            'mb-6': true,
          }"
        />
        <div
          :class="{
            container: true,
          }"
        >
          <div
            :class="{
              'flex flex-wrap justify-center': true,
            }"
          >
            <UssPlanSelectionCard
              v-for="(item, index) in ussPlanSelectionCards"
              :key="index"
              v-bind="item"
              :resolved-plan="planLookup.get(item.selectPlan?.reference?._ref)"
              :is-selected="selectedPlanValue === item.hekateInputValue"
              :is-features-open="isFeaturesOpen(item._key)"
              @select="handlePlanSelect(item.hekateInputValue)"
              @toggle-features="toggleFeatures(item._key)"
            />
          </div>

          <PortableText
            v-if="description?.codeEmbedPortableText"
            :portable-data="description?.codeEmbedPortableText"
            :class="{
              'mt-6': true,
            }"
          />
        </div>
        <slot />
      </div>
    </div>

    <template v-if="screenPartialData?.sectionBuilder?.length > 0">
      <component
        :is="getComponent(component._type)"
        v-for="component in screenPartialData?.sectionBuilder"
        :key="component._key"
        :value="component"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import UssPlanSelectionCard from './UssPlanSelectionCard.vue';
import PortableText from '~/components/Atmos/PortableText/PortableText.vue';
import type {
  PlanRefQueryResult,
  SignupPartialViewsSectionBuilder,
  UssPlanSelection,
} from '~/sanity/types';
import { getComponent } from '~/components/Mapper';
import type UnifiedSignupSessionInfo from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/session-info/UnifiedSignupSessionInfo';
import type ChoosePlanSignupScreen from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/screens/ChoosePlanSignupScreen';
import { FieldHtmlNameUtil } from '@onshape/www-inertia';
import { useMediaQuery } from '@vueuse/core';

const props = defineProps<
  UssPlanSelection & {
    signupScreen: ChoosePlanSignupScreen;
    sessionInfo: UnifiedSignupSessionInfo;
    screenPartialData: SignupPartialViewsSectionBuilder;
  }
>();

const { signupScreen, sessionInfo, ussPlanSelectionCards } = toRefs(props);
const selectedPlanValue = ref<string | undefined>(undefined);
const cardFeaturesOpen = ref<Record<string, boolean>>({});

/** Handle plan card selection */
const handlePlanSelect = (hekateInputValue?: string) => {
  if (!hekateInputValue) return;
  selectedPlanValue.value = hekateInputValue;
  const field = signupScreen.value.getField(FieldHtmlNameUtil.chooseYourOnshapePlan);
  field.val = hekateInputValue;
};

/** Handle show all plans link click */
const handleShowAllPlans = () => {
  const choosePlanField = signupScreen.value.getField(FieldHtmlNameUtil.chooseYourOnshapePlan);
  choosePlanField.val = '';
  choosePlanField.isRequired = false;
  choosePlanField.isValidated = true;
  sessionInfo.value.isChooseOwnPlan = true;
  signupScreen.value.onUpdatePage();
};

/** Toggle features visibility for a specific card */
const isLgScreen = useMediaQuery('(min-width: 1024px)');
const toggleFeatures = (cardKey: string) => {
  cardFeaturesOpen.value[cardKey] = !cardFeaturesOpen.value[cardKey];
};

/** Check if card features are open */
const isFeaturesOpen = (cardKey: string) => {
  return isLgScreen.value ? true : (cardFeaturesOpen.value[cardKey] ?? false);
};
const matchedPlans = ref<(PlanRefQueryResult | null)[]>([]);
// Extract only plans that match ussPlanSelectionCards references
watch(
  ussPlanSelectionCards,
  async () => {
    const planRefs =
      ussPlanSelectionCards.value
        ?.map((card) => card.selectPlan?.reference?._ref)
        .filter((ref): ref is string => !!ref) ?? [];

    matchedPlans.value = await useRefsStore().getUssPlans(planRefs);
    // Auto-select single plan or recommended plan
    if (selectedPlanValue.value) return; // Don't override existing selection

    if (ussPlanSelectionCards.value?.length === 1) {
      handlePlanSelect(ussPlanSelectionCards.value[0]?.hekateInputValue);
    } else {
      const recommended = ussPlanSelectionCards.value?.find((card) => card.recommended);
      if (recommended) {
        handlePlanSelect(recommended.hekateInputValue);
      }
    }
  },
  { immediate: true }
);

const planLookup = computed(() => new Map(matchedPlans.value.map((plan) => [plan?._id, plan])));

const partialDiv = ref<HTMLElement | null>(null);
// Add event listener for show all plans link from PortableText
const onCodeEmbedRendered = () => {
  const showAllPlansLink = partialDiv.value?.querySelector('.show-all-plans-link');
  if (showAllPlansLink) {
    showAllPlansLink.addEventListener(
      'click',
      (e) => {
        e.preventDefault();
        handleShowAllPlans();
      },
      { once: true }
    );
  }
};
onMounted(() => {
  partialDiv.value?.addEventListener('code-embed-rendered', onCodeEmbedRendered);
});
onUnmounted(() => {
  partialDiv.value?.removeEventListener('code-embed-rendered', onCodeEmbedRendered);
});
</script>

<style></style>
